// src/utils/followUpPlansStore.js
// Firestore IO for followUpPlans. One doc per closed deal, id = deal id,
// so re-triggering the Closed hook can never create duplicates. All schedule
// logic lives in followUpPlan.js; this file only moves data.
import {
  collection, doc, getDoc, getDocs, onSnapshot, query, setDoc, updateDoc, where,
} from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";
import {
  buildFollowUpPlan, nextAnniversaryEntry, mergeRevivedTouches, PROGRAM_START,
} from "./followUpPlan";
import { dealContactIds } from "./contactCrm";
import { getContactsByIds } from "./firebasefunctions";

const COL = "followUpPlans";
const VALID_ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// Called when a deal turns Closed. Creates the plan once; also ends any
// still-active plan for the same household (their new closing supersedes it).
// Contacts are fetched here so names denormalize correctly (buildFollowUpPlan
// stays pure); createdAt is stamped here, not in the builder.
export async function ensureFollowUpPlan(deal) {
  // A pre-program closing must never enroll, even if it gets re-picked as
  // "Closed" today (e.g. an old deal touched during cleanup). Missing or
  // garbage closing dates fall through instead — a genuinely new closing can
  // legitimately lack one yet, and buildFollowUpPlan's needsReview path
  // handles that.
  const rawClosing = String(deal?.closingdate ?? "").trim();
  if (VALID_ISO_DATE.test(rawClosing) && rawClosing < PROGRAM_START) return null;
  let contacts = [];
  try {
    contacts = (await getContactsByIds(dealContactIds(deal))) || [];
  } catch {
    // Names fall back to customers[].clientName inside the builder.
  }
  const plan = { ...buildFollowUpPlan(deal, contacts), createdAt: Date.now() };
  if (!plan.dealId) return null;
  const ref = doc(firestored, COL, plan.dealId);
  const existing = await getDoc(ref);
  if (existing.exists()) {
    const prev = existing.data();
    const retracted =
      prev.status === "ended" &&
      String(prev.endedReason || "").startsWith("deal-reopened");
    if (!retracted) return null;
    // Re-closed after a retraction: fresh schedule, but completed touches,
    // the original creation stamp, and a revival marker all survive.
    plan.touches = mergeRevivedTouches(prev.touches, plan.touches);
    if (prev.createdAt) plan.createdAt = prev.createdAt;
    plan.revivedAt = Date.now();
  }

  await setDoc(ref, plan);

  // Best-effort: end any still-active plan for the same household (their new
  // closing supersedes it). array-contains alone is auto-indexed; status is
  // filtered client-side so no composite index is needed. A failure here
  // must never undo the creation above.
  try {
    for (const contactId of plan.clientIds) {
      const snap = await getDocs(
        query(collection(firestored, COL), where("clientIds", "array-contains", contactId)),
      );
      await Promise.all(
        snap.docs
          .filter((d) => d.id !== plan.dealId && d.data().status === "active")
          .map((d) =>
            updateDoc(d.ref, { status: "ended", endedReason: "new-closing" }),
          ),
      );
    }
  } catch {
    // Superseded plans stay active until the next closing retries this.
  }

  return plan;
}

// Live subscription to every plan; role scoping happens in the page
// (agents see their own, Admins see all), matching existing patterns.
export function subscribeFollowUpPlans(cb) {
  return onSnapshot(collection(firestored, COL), (snap) => {
    cb(snap.docs.map((d) => ({ ...d.data(), dealId: d.id })));
  });
}

// Patch one touch. patch example: { state: "done", doneAt: Date.now(),
// doneBy: userId, method: "text" }. Dotted paths so concurrent updates to
// different touches never clobber each other.
export async function updateTouch(planId, touchKey, patch) {
  const updates = {};
  Object.entries(patch).forEach(([k, v]) => {
    updates[`touches.${touchKey}.${k}`] = v;
  });
  await updateDoc(doc(firestored, COL, planId), updates);
}

// After resolving an anniversary (or when one has sat pending 11+ months),
// materialize the next one; a stale pending anniversary gets marked skipped
// so the program never stalls. nextAnniversaryEntry jumps straight past any
// dormancy, so this is always a single write. Returns the applied entry (or
// null) so a caller holding an in-memory copy of `plan` — like the notify
// sweep, which reads it again in the same pass — can correct it instead of
// notifying off what just became stale in Firestore.
export async function rollAnniversaryForward(plan) {
  const entry = nextAnniversaryEntry(plan);
  if (!entry) return null;
  const updates = { [`touches.${entry.key}`]: entry.touch };
  if (entry.staleKey) {
    updates[`touches.${entry.staleKey}.state`] = "skipped";
    updates[`touches.${entry.staleKey}.skippedReason`] = "stale";
  }
  await updateDoc(doc(firestored, COL, plan.dealId), updates);
  return entry;
}

// Best-effort repair for a plan whose household name froze blank because the
// contact fetch failed at closing time. One field, one write; the page's
// onSnapshot subscription repaints the header once it lands.
export async function repairClientNames(planId, clientNames) {
  await updateDoc(doc(firestored, COL, planId), { clientNames });
}

// Team Lead reassigns a plan to a different agent (Team view bulk action).
export async function reassignFollowUpPlan(planId, agentId) {
  await updateDoc(doc(firestored, COL, planId), { agentId });
}

// Deal moved back out of Closed: drop the plan if nothing fired, end it if
// some touches already happened.
export async function retractFollowUpPlan(dealId) {
  const ref = doc(firestored, COL, String(dealId));
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  if (snap.data().status === "ended") return;
  const touched = Object.values(snap.data().touches || {}).some(
    (t) => t.state !== "pending",
  );
  await updateDoc(ref, {
    status: "ended",
    endedReason: touched ? "deal-reopened" : "deal-reopened-clean",
  });
}
